import {
  Stack,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Checkbox,
  Paper,
  Pagination,
  TableSortLabel,
  SxProps,
} from "@mui/material";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  setOpenBulkActionModal,
  setPage,
  setRowsPerPage,
  setSelectedRowsForNegatives,
  setSingleRowIDPassToModal,
  setSort,
} from "../../../../../../../../../../features/scopesTabSlice";
import { useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { Theme } from "@emotion/react";
import { formatNumber } from "../../../../../../../../../../utils/helpers";
import NegativeSearchAdgroupFilterCard from "./NegativeSearchAdgroupFilterCard";

const NegativesInAdgroupTable = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    negativesInListAndInAdGroupData,
    selectedNegativeSearchTerm,
    selectedCampaigns,
    selectedAdgroups,
    selectedRowsForNegatives,
    sort,
    rowsPerPage,
    page,
  } = useSelector((state: any) => state.searchTermOperation);

  const [sortedData, setSortedData] = useState<any[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);

  const tableHeaders = [
    { id: 1, KEY: "search_term", label: "Search Term", sortable: true },
    {
      id: 2,
      KEY: "negative_term_match_type",
      label: "Match Type",
      sortable: true,
    },
    { id: 3, KEY: "adgroup_id", label: "Adgroup ID", sortable: true },
    { id: 4, KEY: "adgroup_name", label: "Adgroup Name", sortable: true },
    { id: 5, KEY: "campaign_id", label: "Campaign ID", sortable: true },
    { id: 6, KEY: "campaign_name", label: "Campaign Name", sortable: true },
    { id: 7, KEY: "list_name", label: "List Name", sortable: true },
    { id: 8, KEY: "last_seen", label: "Last Seen", sortable: true },
    {
      id: 9,
      KEY: "impression_last_3_years",
      label: " IMP LAST 3 YEARS",
      sortable: true,
    },
    { id: 10, KEY: "actions", label: "Actions", sortable: false },
  ];

  // --- Filter logic ---
  const filteredRows = useMemo(() => {
    return (sortedData || []).filter((row: any) => {
      const campaignMatch =
        selectedCampaigns.length > 0
          ? selectedCampaigns.includes(row.campaign_name)
          : true;

      const adgroupMatch =
        selectedAdgroups.length > 0
          ? selectedAdgroups.includes(row.adgroup_name)
          : true;

      const searchMatch = selectedNegativeSearchTerm
        ? row.search_term
            ?.toLowerCase()
            .includes(selectedNegativeSearchTerm?.toLowerCase())
        : true;

      return campaignMatch && adgroupMatch && searchMatch;
    });
  }, [
    sortedData,
    selectedCampaigns,
    selectedAdgroups,
    selectedNegativeSearchTerm,
  ]);

  //-- Sorting Data
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) return 0;

        let aValue = a[keytoSort];
        let bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  useEffect(() => {
    if (!negativesInListAndInAdGroupData) return;
    const modifiedWithUniqueIds = negativesInListAndInAdGroupData.map(
      (row: any) => ({
        ...row,
        id: `${uuidv4()}`,
      })
    );
    const sortedRows = getSortedData(modifiedWithUniqueIds || []);
    setSortedData(sortedRows);
  }, [negativesInListAndInAdGroupData, getSortedData]);

  useEffect(() => {
    if (selectedRowIds.length === 0) return;

    const visibleSelectedIds = selectedRowIds.filter((id) =>
      filteredRows.some((r) => r.id === id)
    );

    if (visibleSelectedIds.length > 0) {
      if (visibleSelectedIds.length !== selectedRowIds.length) {
        setSelectedRowIds(visibleSelectedIds);
        const visibleSelectedRows = filteredRows.filter((r) =>
          visibleSelectedIds.includes(r.id)
        );
        dispatch(setSelectedRowsForNegatives(visibleSelectedRows));
      }
    } else {
      setSelectedRowIds([]);
      dispatch(setSelectedRowsForNegatives([]));
    }
    // eslint-disable-next-line
  }, [filteredRows]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = paginatedRows.map((row: any) => row.id);
      setSelectedRowIds(newSelected);
      dispatch(setSelectedRowsForNegatives(paginatedRows));
    } else {
      setSelectedRowIds([]);
      dispatch(setSelectedRowsForNegatives([]));
    }
  };

  const handleSelectRow = (id: number) => {
    const selectedIndex = selectedRowIds.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedRowIds, id];
    } else {
      newSelected = selectedRowIds.filter((rowId) => rowId !== id);
    }

    setSelectedRowIds(newSelected);
    const selectedRows = filteredRows.filter((row: any) =>
      newSelected.includes(row.id)
    );
    dispatch(setSelectedRowsForNegatives(selectedRows));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    dispatch(setPage(newPage));
    const updatedParams = new URLSearchParams(window.location.search);
    updatedParams.set("page", newPage.toString());
    setSearchParams(updatedParams);
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForNegatives([]));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(0));
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForNegatives([]));
  };

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return;
    dispatch(
      setSort({
        keytoSort: header.KEY,
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  useEffect(() => {
    dispatch(setPage(0));
    // eslint-disable-next-line
  }, [selectedAdgroups, selectedCampaigns, selectedNegativeSearchTerm]);

  useEffect(() => {
    setSelectedRowIds([]);
    dispatch(setSelectedRowsForNegatives([]));
    dispatch(setPage(Number(searchParams.get("page")) || 0));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page"));
    dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl));
    // eslint-disable-next-line
  }, [searchParams]);

  useEffect(() => {
    if (selectedRowsForNegatives?.length === 0) {
      setSelectedRowIds([]);
    }
  }, [selectedRowsForNegatives]);

  return (
    <>
      <NegativeSearchAdgroupFilterCard />

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          mt: 2,
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 5, // a higher zIndex ensures it sits above table rows
                  backgroundColor: "background.paper",
                }}
              >
                <Checkbox
                  color="secondary"
                  indeterminate={
                    selectedRowIds.length > 0 &&
                    selectedRowIds.length < paginatedRows.length
                  }
                  checked={
                    paginatedRows.length > 0 &&
                    selectedRowIds.length === paginatedRows.length &&
                    selectedRowsForNegatives.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              {tableHeaders.map((header: any) => {
                // Which columns should be sticky
                const isLeftSticky = header.id === 1 || header.id === 2;
                const isRightSticky =
                  header.id === tableHeaders[tableHeaders.length - 1].id; // last column

                const leftOffsets: Record<number, number> = {
                  1: 52,
                  2: 162,
                };

                const cellSx = isLeftSticky
                  ? ({
                      position: "sticky",
                      left: leftOffsets[header.id] ?? 0,
                      zIndex: 10,
                      backgroundColor: "background.paper",
                      borderRight:
                        header.id === 2 ? "1px solid #e0e0e0" : undefined,
                    } as SxProps<Theme>)
                  : isRightSticky
                  ? ({
                      position: "sticky",
                      right: 0,
                      zIndex: 9,
                      backgroundColor: "background.paper",
                      borderLeft: "1px solid #e0e0e0",
                    } as SxProps<Theme>)
                  : ({} as SxProps<Theme>);

                return (
                  <TableCell key={header.id} align="left" sx={cellSx}>
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={
                        sort.keytoSort === header.KEY ? sort.direction : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.length > 0 ? (
              paginatedRows.map((row: any) => {
                const isSelected = selectedRowIds.includes(row.id);
                return (
                  <TableRow
                    hover
                    key={row.id}
                    selected={isSelected}
                    // onClick={() => handleSelectRow(row.id)}
                    sx={{
                      cursor: "pointer",
                      backgroundColor: isSelected
                        ? "rgba(156, 39, 176, 0.08) !important"
                        : "inherit",
                      "&:hover": {
                        backgroundColor: isSelected ? "" : "inherit !important",
                      },
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        backgroundColor: isSelected
                          ? "#f0def3"
                          : "background.paper",
                      }}
                    >
                      <Checkbox
                        color="secondary"
                        checked={isSelected}
                        onChange={() => handleSelectRow(row.id)}
                        // onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 52,
                        zIndex: 1,
                        whiteSpace: "normal",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                        backgroundColor: isSelected
                          ? "#f0def3"
                          : "background.paper",
                      }}
                    >
                      {row.search_term ? row.search_term : "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 162,
                        zIndex: 1,
                        whiteSpace: "normal",
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                        backgroundColor: isSelected
                          ? "#f0def3"
                          : "background.paper",
                        borderRight: "1px solid #e0e0e0",
                      }}
                    >
                      {row.negative_term_match_type
                        ? row.negative_term_match_type
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {row.adgroup_id ? row.adgroup_id : "-"}
                    </TableCell>
                    <TableCell>
                      {row.adgroup_name ? row.adgroup_name : "-"}
                    </TableCell>
                    <TableCell>
                      {row.campaign_id ? row.campaign_id : "-"}
                    </TableCell>
                    <TableCell>
                      {row.campaign_name ? row.campaign_name : "-"}
                    </TableCell>
                    <TableCell>{row.list_name ? row.list_name : "-"}</TableCell>
                    <TableCell>
                      {row.last_seen
                        ? dayjs(row.last_seen).format("DD/MM/YYYY")
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {row.impression_last_3_years
                        ? formatNumber(row.impression_last_3_years)
                        : "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 2,
                        backgroundColor: isSelected
                          ? "#f0def3"
                          : "background.paper",
                        borderLeft: "1px solid #e0e0e0",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        // onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="Remove NST from Adgroup">
                          <IconButton
                            disabled={!isSelected}
                            size="small"
                            color={isSelected ? "error" : "default"}
                            onClick={() => {
                              // event.stopPropagation();
                              dispatch(setOpenBulkActionModal(true));
                              dispatch(setSingleRowIDPassToModal(row.id));
                            }}
                          >
                            <RemoveCircleOutlineOutlinedIcon
                              data-tracker="503"
                              fontSize="small"
                            />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeaders.length + 1} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 1 }}
      >
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
              {
                display: "none",
              },
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-toolbar": { paddingLeft: "16px" },
          }}
        />

        <Pagination
          count={Math.ceil(filteredRows.length / rowsPerPage)}
          onChange={(_, value) => handleChangePage(null, value - 1)}
          page={page + 1}
          showFirstButton
          showLastButton
          color="secondary"
          size="small"
        />

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-input": { marginRight: "15px" },
          }}
        />
      </Stack>
    </>
  );
};

export default NegativesInAdgroupTable;
